/**
 * Lightweight typo-tolerant matching for ingredient search.
 *
 * Score tiers (lower is better, -1 = no match):
 *   0  exact substring match
 *   1  every query token is a substring of some word
 *   2  edit distance from the query to the whole text (or any word) is
 *      within a length-dependent allowance (1 for <5 chars, 2 for <9, else 3)
 */

function levenshtein(a: string, b: string, maxDist: number): number {
  if (Math.abs(a.length - b.length) > maxDist) return maxDist + 1;
  let prev = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    let cur: number[] = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      cur.push(v);
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > maxDist) return maxDist + 1; // early bail
    prev = cur;
  }
  return prev[b.length];
}

function allowedDist(q: string): number {
  if (q.length < 5) return 1;
  if (q.length < 9) return 2;
  return 3;
}

/** -1 = no match; 0/1/2 = match quality (lower is better). */
export function fuzzyScore(query: string, text: string): number {
  const q = query.trim().toLowerCase();
  const t = (text || "").toLowerCase();
  if (!q || !t) return -1;
  if (t.includes(q)) return 0;

  const tokens = q.split(/\s+/);
  const words = t.split(/[\s\-_/(),]+/).filter(Boolean);
  if (tokens.length > 1 && tokens.every((tok) => t.includes(tok))) return 1;
  if (tokens.every((tok) => words.some((w) => w.includes(tok)))) return 1;

  const max = allowedDist(q);
  if (levenshtein(q, t, max) <= max) return 2;
  if (words.some((w) => levenshtein(q, w, max) <= max)) return 2;
  return -1;
}

/** Best fuzzy score across several candidate fields; -1 if none match. */
export function fuzzyScoreFields(query: string, fields: Array<string | null | undefined>): number {
  let best = -1;
  for (const f of fields) {
    if (f == null) continue;
    const s = fuzzyScore(query, String(f));
    if (s === 0) return 0;
    if (s > 0 && (best === -1 || s < best)) best = s;
  }
  return best;
}
